/**
 * Formatação dos painéis executivos (pt-BR, sempre via `Intl` — nada de `toFixed`).
 *
 * Os números chegam crus do snapshot; a tela decide só como mostrar.
 */

const nf0 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const nfCompact = new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 });
const nfPct1 = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const nfBRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const nfBRL2 = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** `22058457` → `22.058.457`. */
export const fmtUn = (v: number): string => nf0.format(v);

/** `22058457` → `22,1 mi`. Para eixo, KPI e rótulo de barra. */
export const fmtCompacto = (v: number): string => nfCompact.format(v);

/** `22838437.19` → `R$ 22.838.437`. */
export const fmtBRL = (v: number): string => nfBRL.format(v);

/** `22838437.19` → `R$ 22,8 mi`. */
export const fmtBRLCompacto = (v: number): string => `R$ ${nfCompact.format(v)}`;

/** Preço médio: `0.7333` → `R$ 0,73`. */
export const fmtPreco = (v: number | null): string => (v === null ? '—' : nfBRL2.format(v));

/** Diferença em unidades com sinal: `-8000` → `−8.000`; `0` → `0`; `null` → `—`. */
export function fmtUnSinal(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return '—';
  const arredondado = Math.round(v);
  if (arredondado === 0) return '0';
  return `${arredondado > 0 ? '+' : '−'}${nf0.format(Math.abs(arredondado))}`;
}

/** Desvio com sinal explícito: `-28` → `−28,0%`; `null` → `—`. */
export function fmtDesvio(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return '—';
  if (v === 0) return '0,0%';
  // U+2212 (sinal de menos) alinha com o "+" em fonte proporcional.
  return `${v > 0 ? '+' : '−'}${nfPct1.format(Math.abs(v))}%`;
}

/** `72.4` → `72,4%`. */
export const fmtPct = (v: number | null): string =>
  v === null || !Number.isFinite(v) ? '—' : `${nfPct1.format(v)}%`;

const nfDia = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

/** Prazo em dias corridos: `9.4` → `9,4 d`; `null` → `—`. */
export const fmtDias = (v: number | null): string => (v === null ? '—' : `${nfDia.format(v)} d`);

/**
 * Há quanto tempo um lote espera, legível também para a ficha esquecida de 2020:
 * `12` → `há 12 d`; `95` → `há 3 meses`; `2437` → `há 6,7 anos`.
 */
export function fmtEspera(dias: number): string {
  if (dias < 60) return `há ${nfDia.format(dias)} d`;
  if (dias < 365) return `há ${Math.floor(dias / 30)} meses`;
  return `há ${nfDia.format(Math.round((dias / 365) * 10) / 10)} anos`;
}

/** `2026-09-09` → `09/09`. Aceita também ISO com hora. */
export function fmtDiaMes(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}`;
}

/** `2026-09-09` → `09/09/2026`. */
export function fmtData(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

/** ISO com hora → `10/09/2026 09:49` no fuso do navegador. */
export function fmtDataHora(iso: string | null): string {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/** Período de um mês de produção (16 a 15): `{start: '2026-08-16', end: '2026-09-15'}` → `16/08–15/09`. */
export function periodoCurto(m: { start: string; end: string }): string {
  return `${fmtDiaMes(m.start)}–${fmtDiaMes(m.end)}`;
}

/** Período de um mês civil (DRE): `2026-09` → `01/09–30/09`. */
export function periodoMesCivil(chave: string): string {
  const [ano, mes] = chave.split('-').map(Number);
  const ultimo = new Date(Date.UTC(ano, mes, 0)).getUTCDate();
  const mm = String(mes).padStart(2, '0');
  return `01/${mm}–${ultimo}/${mm}`;
}

/** `Setembro/2026` → `Set/26`. Para eixo e chip, onde o nome inteiro não cabe. */
export function mesCurto(label: string): string {
  const [nome, ano] = label.split('/');
  return `${nome.slice(0, 3)}/${(ano ?? '').slice(-2)}`;
}
